import { CloseIcon } from '@chakra-ui/icons';
import {
  Box,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  IconButton,
  useBreakpointValue,
  useColorModeValue,
} from '@chakra-ui/react';
import { forwardRef, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { safeDynamic } from '@/utils/safeDynamic';
import 'react-datepicker/dist/react-datepicker.css';

export interface DateTimePickFieldProps {
  id?: string;
  label?: string | React.ReactNode;
  name: string;
  errors?: string | string[] | Record<string, any> | null;
  touched?: boolean;
  isRequired?: boolean;
  fontSize?: string | number;
  fontWeight?: string;
  italic?: boolean;
  isDisabled?: boolean;
  labelFontSize?: string;
  [x: string]: any;
}

const DatePicker = safeDynamic<any>(() => import('react-datepicker'), {
  ssr: false,
});

const parseDateTime = (dateTimeStr?: string | null): Date | null => {
  if (!dateTimeStr) return null;

  try {
    if (typeof dateTimeStr === 'string' && /^\d{12}$/.test(dateTimeStr)) {
      const year = parseInt(dateTimeStr.substring(0, 4), 10);
      const month = parseInt(dateTimeStr.substring(4, 6), 10) - 1; // Month is 0-indexed
      const day = parseInt(dateTimeStr.substring(6, 8), 10);
      const hours = parseInt(dateTimeStr.substring(8, 10), 10);
      const minutes = parseInt(dateTimeStr.substring(10, 12), 10);

      const date = new Date(year, month, day, hours, minutes);

      if (!isNaN(date.getTime())) {
        return date;
      }
    }

    const date = new Date(dateTimeStr);
    if (!isNaN(date.getTime())) {
      return date;
    }

    return null;
  } catch (e) {
    console.error('Error parsing datetime:', e, dateTimeStr);
    return null;
  }
};

const formatDateTimeToString = (date: Date): string => {
  const year = date.getFullYear().toString();
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const day = date.getDate().toString().padStart(2, '0');
  const hours = date.getHours().toString().padStart(2, '0');
  const minutes = date.getMinutes().toString().padStart(2, '0');

  return `${year}${month}${day}${hours}${minutes}`;
};

const areSameDateTimes = (date1: Date | null, date2: Date | null): boolean => {
  if (!date1 || !date2) return false;
  return date1.getTime() === date2.getTime();
};

function useDateTimePicker(value: string | null | undefined, setFieldValue: (value: any) => void) {
  const [selectedDateTime, setSelectedDateTime] = useState<Date | null>(parseDateTime(value));
  const userInteractionRef = useRef(false);
  const preventEffectRef = useRef(false);

  useEffect(() => {
    if (preventEffectRef.current) {
      preventEffectRef.current = false;
      return;
    }

    if (!userInteractionRef.current) {
      const newDateTime = parseDateTime(value);

      if (!areSameDateTimes(newDateTime, selectedDateTime)) {
        setSelectedDateTime(newDateTime);
      }
    }
  }, [value, selectedDateTime]);

  const handleDateTimeChange = useCallback(
    (selectedDate: Date | null) => {
      userInteractionRef.current = true;
      preventEffectRef.current = true;

      try {
        setSelectedDateTime(selectedDate);

        if (selectedDate) {
          setFieldValue(formatDateTimeToString(selectedDate));
        } else {
          setFieldValue(null);
        }
      } finally {
        userInteractionRef.current = false;
      }
    },
    [setFieldValue],
  );

  const clearDateTime = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      userInteractionRef.current = true;
      preventEffectRef.current = true;

      setSelectedDateTime(null);
      setFieldValue(null);

      userInteractionRef.current = false;
    },
    [setFieldValue],
  );

  return {
    selectedDateTime,
    handleDateTimeChange,
    clearDateTime,
    userInteractionRef,
  };
}

export function DateTimePickField(props: DateTimePickFieldProps) {
  const {
    id,
    label,
    mb,
    isRequired,
    errors,
    touched,
    fontSize,
    fontWeight,
    italic,
    setFieldValue,
    value,
    isDisabled,
    labelFontSize,
    setTouched,
    h,
    bg,
    borderColor,
    border,
    labelMb = 4,
    onDark,
  } = props;

  const [lightColor, darkColor] = onDark
    ? ['white', 'chakra-body-text']
    : ['chakra-body-text', 'white'];

  const textColorPrimary = useColorModeValue(lightColor, darkColor);
  const responsiveFontSize = useBreakpointValue({
    base: fontSize || '16px',
    md: fontSize || '20px',
  });
  const dateFormat = useBreakpointValue({
    base: {
      day: 'numeric' as const,
      month: 'numeric' as const,
      year: '2-digit' as const,
    },
    md: {
      day: 'numeric' as const,
      month: 'long' as const,
      year: 'numeric' as const,
    },
  });

  const { selectedDateTime, handleDateTimeChange, clearDateTime, userInteractionRef } =
    useDateTimePicker(value, setFieldValue);

  const formatDisplayTime = useCallback((date: Date | null) => {
    if (!date) return 'Select time';

    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    return `${hours}${minutes}`;
  }, []);

  const formatDisplayDate = useCallback(
    (date: Date | null) => {
      if (!date) return 'Select date';

      const options = dateFormat || {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      };

      const formatted = date.toLocaleDateString('en-GB', options);
      return options.year === '2-digit' ? formatted.replace(/\//g, '-') : formatted;
    },
    [dateFormat],
  );

  const timeDisplay = useMemo(
    () => formatDisplayTime(selectedDateTime),
    [selectedDateTime, formatDisplayTime],
  );

  const dateDisplay = useMemo(
    () => formatDisplayDate(selectedDateTime),
    [selectedDateTime, formatDisplayDate],
  );

  const getErrorString = useCallback((): string | undefined => {
    if (!errors) return undefined;
    if (typeof errors === 'string') return errors;
    if (Array.isArray(errors)) return errors[0];
    if (typeof errors === 'object') {
      const errorKeys = Object.keys(errors as Record<string, any>);
      if (errorKeys.length > 0) {
        const firstError = (errors as Record<string, any>)[errorKeys[0]];
        return typeof firstError === 'string' ? firstError : undefined;
      }
    }
    return undefined;
  }, [errors]);

  const errorMessage = useMemo(() => getErrorString(), [getErrorString]);

  const CustomInput = useMemo(
    () =>
      forwardRef<HTMLDivElement, any>(({ onClick }, ref) => (
        <Flex
          ref={ref}
          w="100%"
          border={border || '3px solid'}
          borderColor={borderColor || (isDisabled ? 'gray' : 'white')}
          borderRadius="10px"
          bg={bg || 'blue.800'}
          h={h || '60px'}
          maxH="60px"
          onClick={isDisabled ? undefined : onClick}
          cursor={isDisabled ? 'not-allowed' : 'pointer'}
        >
          {/* Left side - Time */}
          <Flex
            flex="1"
            align="center"
            justify="flex-start"
            p="0 15px"
            pl={{ base: '15px', md: '30px' }}
            pr={{ base: '15px', md: '30px' }}
            borderRight="2px solid"
            borderColor={borderColor || (isDisabled ? 'gray' : 'white')}
            color={isDisabled ? 'gray' : textColorPrimary}
            fontSize={responsiveFontSize}
            fontWeight={fontWeight || '700'}
            style={{ fontStyle: italic ? 'italic' : 'normal' }}
            minWidth="50%"
            height="100%"
          >
            <Box as="span" whiteSpace="nowrap" minWidth="100%">
              {timeDisplay}
            </Box>
          </Flex>

          {/* Right side - Date */}
          <Flex
            flex="1"
            align="center"
            justify="space-between"
            p="0 15px"
            pl={{ base: '15px', md: '30px' }}
            pr={{ base: '15px', md: '30px' }}
            color={isDisabled ? 'gray' : textColorPrimary}
            fontSize={responsiveFontSize}
            fontWeight={fontWeight || '700'}
            style={{ fontStyle: italic ? 'italic' : 'normal' }}
            position="relative"
            minWidth="50%"
            height="100%"
          >
            <Box as="span" whiteSpace="nowrap" flex="1">
              {dateDisplay}
            </Box>

            {selectedDateTime && !isDisabled && (
              <IconButton
                aria-label="Clear date and time"
                icon={<CloseIcon />}
                size="xs"
                variant="ghost"
                color={isDisabled ? 'gray' : textColorPrimary}
                onClick={clearDateTime}
                _hover={{ bg: 'blue.600' }}
                ml={2}
                minWidth="auto"
              />
            )}
          </Flex>
        </Flex>
      )),
    [
      border,
      borderColor,
      isDisabled,
      bg,
      h,
      responsiveFontSize,
      fontWeight,
      italic,
      timeDisplay,
      dateDisplay,
      selectedDateTime,
      clearDateTime,
    ],
  );

  return (
    <Flex direction="column" mb={mb || '30px'} width="full" pos="relative">
      <FormControl
        isRequired={isRequired}
        isInvalid={!!errorMessage && touched}
        isDisabled={isDisabled}
        width="100%"
      >
        {label && (
          <FormLabel
            display="flex"
            htmlFor={id}
            fontSize={labelFontSize || '2xl'}
            lineHeight="normal"
            color={textColorPrimary}
            fontWeight="bold"
            mb={labelMb}
            _hover={{ cursor: 'pointer' }}
          >
            {label}
          </FormLabel>
        )}

        <Box w="100%">
          <DatePicker
            selected={selectedDateTime}
            onChange={handleDateTimeChange}
            showTimeSelect
            timeFormat="HH:mm"
            timeIntervals={15}
            timeCaption="Time"
            dateFormat="MMMM d, yyyy HH:mm"
            disabled={isDisabled}
            onCalendarOpen={() => {
              setTouched && setTouched(true);
              userInteractionRef.current = true;
            }}
            customInput={<CustomInput />}
          />
        </Box>

        {errorMessage && touched && <FormErrorMessage ml="10px">{errorMessage}</FormErrorMessage>}
      </FormControl>
    </Flex>
  );
}
